import { useCallback } from "react";

import {
  ConnectionDocument,
  CreateOperationInput,
  FindOneSyncQuery,
  ListSyncsDocument,
  OperationModes,
  OperationTypes,
  ReverseEtlSyncFragment,
  useCreateReverseEtlSyncMutation,
} from "@/apollo/types";
import { useCallbackRef } from "@/hooks/useCallbackRef";
import { useCurrentAccount } from "@/providers/account";

import invariant from "tiny-invariant";
import {
  ModelMappingState,
  PropertyMapping,
  ReverseETLStepState,
} from "./state/StepStateContext";

const generateConfig = (
  connectionSettings: Record<string, any> | undefined,
  objectTypeSettings: Record<string, any> | undefined,
) => {
  const settings = {
    ...connectionSettings,
    ...objectTypeSettings,
  };
  if (Object.keys(settings).length === 0) {
    return JSON.stringify({});
  }
  return JSON.stringify({
    destinationSettings: settings,
  } satisfies FindOneSyncQuery["findOneSync"]["config"]);
};

const toMapping = (
  item: PropertyMapping,
  isPrimaryKey: boolean,
  operationMode: OperationModes,
  objectTypeName: string,
) => {
  return {
    type: OperationTypes.Primary,
    operation: operationMode,
    object: objectTypeName,
    sourcePropertyId: item.sourceProperty.name,
    primaryKey: isPrimaryKey,
    sourcePropertyName: item.sourceProperty.name,
    sourcePropertyType: item.sourceProperty.type,
    destinationPropertyId: item.destinationProperty.value,
    destinationPropertyName: item.destinationProperty.label,
    destinationPropertyType:
      item.destinationProperty.meta?.propertyType || "string", // TODO: should this just be the type of the source property?
    readonly: item.destinationProperty.meta?.readonly || false,
  };
};

const generateOperations = (
  integrationId: string,
  modelMapping: ModelMappingState,
) => {
  const operation: CreateOperationInput = {
    id: modelMapping.operation.id,
    integrationId: integrationId,
    objectType: modelMapping.objectType.objectName,
    mappings: modelMapping.mappings.map((item) =>
      toMapping(
        item,
        false,
        modelMapping.operation.operationMode,
        modelMapping.objectType.objectName,
      ),
    ),
  };

  if (
    modelMapping.primaryMapping &&
    modelMapping.operation.primaryKeyRequired
  ) {
    operation.mappings.unshift(
      toMapping(
        modelMapping.primaryMapping,
        true,
        modelMapping.operation.operationMode,
        modelMapping.objectType.objectName,
      ),
    );
  }

  return [operation];
};

export function useCreateSync(options: {
  onSyncCreated: (sync: ReverseEtlSyncFragment) => void;
  onSyncError: (error: Error) => void;
}) {
  const dataWarehouseConnectionId =
    useCurrentAccount().dataWarehouseConnectionId;

  const onSyncCreated = useCallbackRef(options.onSyncCreated);
  const onSyncError = useCallbackRef(options.onSyncError);

  const [createSyncMutation, { loading: isCreatingSync }] =
    useCreateReverseEtlSyncMutation();

  const createSync = useCallback(
    async (state: ReverseETLStepState) => {
      const destinationValues = state.destination.value;
      invariant(destinationValues?.connection, "Connection is required");

      const syncSettingsValues = state.syncSettings.value;
      invariant(syncSettingsValues, "Sync settings are required");

      const mappingValues = state.modelMapping.value;
      invariant(mappingValues, "Mapping values are required");
      invariant(mappingValues.source, "Source is required");

      const operationMode = mappingValues.operation;
      invariant(
        !operationMode.primaryKeyRequired || mappingValues.primaryMapping,
        "Primary mapping is required",
      );
      try {
        const { data, errors } = await createSyncMutation({
          variables: {
            destinationConnectionId: destinationValues.connection.id,
            sourceId: mappingValues.source.value,
            sourceType: mappingValues.source.type,
            operations: generateOperations(
              destinationValues.connection.integrationId,
              mappingValues,
            ),
            primaryObject: mappingValues.objectType.objectName,
            schedulerType: syncSettingsValues.schedulerType,
            sourceConnectionId: dataWarehouseConnectionId!,
            config: generateConfig(
              state.connectionSettings.value?.settings,
              mappingValues.objectTypeSettings,
            ),
            orchestrationWorkflowId: syncSettingsValues.orchestrationWorkflowId,
            syncInterval: syncSettingsValues.scheduleKey,
          },
          refetchQueries: [
            { query: ListSyncsDocument },
            {
              query: ConnectionDocument,
              variables: { id: destinationValues.connection.id },
            },
          ],
        });

        if (data) {
          onSyncCreated(data.createReverseEtlSync);
        } else {
          onSyncError(
            new Error(
              errors?.[0]?.message ||
                "Unknown error creating sync. Please try again.",
            ),
          );
        }
      } catch (error) {
        onSyncError(error as Error);
      }
    },
    [createSyncMutation, onSyncCreated, onSyncError, dataWarehouseConnectionId],
  );

  return {
    createSync,
    isCreatingSync,
  };
}
